/**
 * Notifications hooks. API data only when authenticated (errors → isError +
 * empty). The unread count is derived from the list (no dedicated count
 * endpoint). Mark-read / mark-all-read hit the API.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { NOTIFICATIONS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { NotificationItem } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { notificationsApi } from "./notifications.api";
import { toNotification } from "./notifications.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseNotificationsResult {
  notifications: NotificationItem[];
  isLoading: boolean;
  isError: boolean;
  source: DataSource;
  refetch: () => void;
}

export function useNotifications(): UseNotificationsResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.notificationsList(LIST_PARAMS),
    queryFn: () => notificationsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data) return { notifications: query.data.data.map(toNotification), isLoading: false, isError: false, source: "api", refetch: query.refetch };
  if (query.isError) return { notifications: [], isLoading: false, isError: true, source: "api", refetch: query.refetch };
  return { notifications: [], isLoading: query.isLoading, isError: false, source: "api", refetch: query.refetch };
}

export function useUnreadNotificationsCount(): number {
  const { notifications } = useNotifications();
  return notifications.filter((n) => !n.read).length;
}

function useInvalidateNotifications() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_PREFIX });
}

export function useMarkNotificationRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: async (id: string) => {
      return notificationsApi.markRead(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useMarkAllNotificationsRead() {
  const invalidate = useInvalidateNotifications();
  return useMutation({
    mutationFn: async () => {
      return notificationsApi.markAllRead();
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
